<script setup>
import { computed, ref } from 'vue'
import Chip from 'primevue/chip'
import Button from 'primevue/button'
import { useToast } from 'primevue/usetoast'
import ConfirmDeleteDialog from '@/components/common/ConfirmDeleteDialog.vue'
import AddResponsibleDialog from '@/components/common/AddResponsibleDialog.vue'
import { useResponsiblesStore } from '@/stores/responsibles.js'

/*
  Универсальный компонент отображения и управления ответственными:
  - Список чипов: поддерживает форматы строк/чисел, произвольных объектов и ResponsibilityItem (из /responsibles/.../current)
  - Иконка «звезда» для основного и #приоритет
  - Удаление через кнопку внутри чипа + ConfirmDeleteDialog (без авто-скрытия чипа)
  - Кнопка «+» справа в контейнере для добавления через AddResponsibleDialog
    • Кнопка автоматически блокируется при existingCount ≥ 3
    • Диалог получает existingCount; логика «сделать основным, даже если уже есть» реализуется чекбоксом в самом диалоге
*/

const props = defineProps({
  list: { type: Array, default: () => [] },
  labelKey: { type: String, default: 'full_name' },
  singularTitle: { type: String, default: 'Ответственный' },
  pluralTitle: { type: String, default: 'Ответственные' },
  emptyText: { type: String, default: '—' },

  removable: { type: Boolean, default: false },

  showPrimaryIcon: { type: Boolean, default: true },
  showPriority: { type: Boolean, default: true },
  primaryIconClass: { type: String, default: 'pi pi-star text-yellow-400' },

  entityType: { type: String, default: '' }, // 'work'|'facility'|'contract'|'customer'
  entityId: { type: [Number, String], default: null },

  allowAdd: { type: Boolean, default: false },
})

const emit = defineEmits(['remove'])


const itemsForUI = computed(() => {
  return (props.list || [])
      .map((it, idx) => {
        if (typeof it === 'string' || typeof it === 'number') {
          return {
            key: `p-${idx}-${String(it)}`,
            text: String(it),
            isPrimary: false,
            priority: null,
            userId: null,
            original: it,
          }
        }
        if (it && typeof it === 'object' && it.user && (it.user.full_name || it.user.id)) {
          // ResponsibilityItem
          return {
            key: `u-${it.user.id ?? idx}`,
            text: it.user.full_name ?? String(it.user.id),
            isPrimary: !!it.is_primary,
            priority: it.priority ?? null,
            userId: it.user.id ?? null,
            original: it,
          }
        }
        // Произвольный объект
        const labelByKey = it?.[props.labelKey]
        const text = labelByKey ?? it?.name ?? it?.title ?? (it?.id != null ? String(it.id) : null)
        return {
          key: `o-${it?.id ?? idx}`,
          text: text != null ? String(text) : tryStringify(it),
          isPrimary: !!it?.is_primary,
          priority: it?.priority ?? null,
          userId: it?.user_id ?? it?.id ?? null,
          original: it,
        }
      })
      .filter((x) => !!x.text)
})

function tryStringify(v) {
  try {
    return JSON.stringify(v)
  } catch {
    return ''
  }
}

const hasData = computed(() => itemsForUI.value.length > 0)
const titleText = computed(() => (itemsForUI.value.length === 1 ? props.singularTitle : props.pluralTitle))

const canRemove = computed(() => props.removable || (!!props.entityType && !!props.entityId))

const existingCount = computed(() => (Array.isArray(props.list) ? props.list.length : 0))
const maxCount = 3
const addDisabled = computed(() => existingCount.value >= maxCount)

const showAddDialog = ref(false)
function openAdd() {
  if (!props.entityType || !props.entityId) return
  if (addDisabled.value) return
  showAddDialog.value = true
}

const showConfirm = ref(false)
const toRemove = ref(null) // { userId, priority, label }
const toast = useToast()
const responsiblesStore = useResponsiblesStore()

function askUnassign(item) {
  emit('remove', { item: item.original })
  if (!props.entityType || !props.entityId) return
  toRemove.value = {
    userId: item.userId || null,
    priority: item.priority || null,
    label: item.text,
  }
  showConfirm.value = true
}

async function confirmUnassign() {
  if (!props.entityType || !props.entityId || !toRemove.value) return
  try {
    const payload = {}
    if (toRemove.value.userId) payload.user_id = toRemove.value.userId
    else if (toRemove.value.priority) payload.priority = toRemove.value.priority
    await responsiblesStore.unassign(props.entityType, Number(props.entityId), payload)
    toast?.add?.({ severity: 'success', summary: 'Снят ответственный', detail: toRemove.value.label, life: 2000 })
  } catch (e) {
    console.error('Не удалось снять ответственного:', e)
    toast?.add?.({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось снять ответственного', life: 3500 })
  }
}
</script>

<template>
  <div class="w-full">
    <div class="flex items-start justify-between gap-2 w-full">
      <div class="flex-1 min-w-0">
        <template v-if="hasData">
          <span class="font-semibold">{{ titleText }}: </span>
          <span class="inline-flex gap-2 flex-wrap ml-2">
            <Chip v-for="item in itemsForUI" :key="item.key" class="px-1 py-0 chip-tight">
              <template #default>
                <span class="inline-flex items-center gap-1">
                  <i v-if="showPrimaryIcon && item.isPrimary" :class="primaryIconClass" />
                  <span>{{ item.text }}</span>
                  <span v-if="showPriority && item.priority" class="text-xs text-gray-500">#{{ item.priority }}</span>
                  <!-- Кнопка удаления -->
                  <Button
                      v-if="canRemove"
                      icon="pi pi-times"
                      rounded
                      text
                      size="small"
                      class="p-0 h-5 w-5 ml-1 -mr-1"
                      @click.stop="askUnassign(item)"
                      aria-label="Удалить"
                      title="Удалить"
                  />
                </span>
              </template>
            </Chip>
          </span>
        </template>
        <template v-else>
          <span class="font-semibold">{{ pluralTitle }}: </span>{{ ' ' + emptyText }}
        </template>
      </div>

      <div class="shrink-0 mt-0.5">
        <Button
            v-if="allowAdd && entityType && entityId"
            icon="pi pi-plus"
            rounded
            text
            size="small"
            :disabled="addDisabled"
            :title="addDisabled ? 'Доступно максимум 3 ответственных' : 'Добавить ответственного'"
            @click="openAdd"
        />
      </div>
    </div>

    <ConfirmDeleteDialog
        v-model:visible="showConfirm"
        :employeeName="toRemove?.label || ''"
        @confirm="confirmUnassign"
    />

    <AddResponsibleDialog
        v-if="allowAdd && entityType && entityId"
        v-model:visible="showAddDialog"
        :entityType="entityType"
        :entityId="Number(entityId)"
        :existingCount="existingCount"
        @saved="() => {}"
    />
  </div>
</template>

<style scoped>
:deep(.p-chip) {
  padding: 2px 6px;
  min-height: 20px;
  line-height: 18px;
}
:deep(.p-chip .p-chip-content) {
  display: flex;
  align-items: center;
}
:deep(.p-chip .p-chip-text) {
  font-size: 0.875rem;
}
.chip-tight {
  padding-top: 2px;
  padding-bottom: 2px;
}
</style>